import React, { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import DashboardLayout from "core/layouts/DashboardLayout"
import Input from "core/components/Input"
import Button from "core/components/Button"
import BatchChangeViewModel from "./BatchChangeViewModel"

export default function BatchChange() {
  const {
    loaderRef,
    observerRef,
    search,
    expandedBatchId,
    hasMore,
    page,
    isLoading,
    auth,
    batchesToDisplay,
    fetchBatchStudentDetails,
    handleSearch,
    setGetAllBatchesFilter,
    toggleBatchStatus,
    searchBatchByName,
    formatDate,
    fetchAllBatches,
    handleDropdownToggle,
    GetBatchStats,
    handleKeyPress,
    setPage,
    setSearch,
  } = BatchChangeViewModel()

  const navigate = useNavigate()

  const handleViewBatch = (batch: any) => {
    fetchBatchStudentDetails(batch.id)
    navigate(`/admin/batch-students-view/${batch.id}`)
  }

  useEffect(() => {
    if (expandedBatchId) {
      GetBatchStats(auth, expandedBatchId)
    }
  }, [expandedBatchId])

  useEffect(() => {
    fetchAllBatches(1)
  }, [])

  useEffect(() => {
    if (!loaderRef.current) return

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          setPage((prev) => prev + 1)
        }
      },
      { threshold: 0.5 }
    )

    observerRef.current.observe(loaderRef.current)

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [hasMore, isLoading])

  useEffect(() => {
    if (!search.trim() && page > 1) {
      fetchAllBatches(page)
    }
  }, [page])

  useEffect(() => {
    if (!search.trim()) {
      searchBatchByName(search)
    }
  }, [search])

  useEffect(() => {
    if (!search.trim()) {
      setGetAllBatchesFilter([])
    }
  }, [search])

  return (
    <DashboardLayout>
      <div className="flex justify px-[24px] py-[16px] space-y-6">
        <div className="w-[795px]">
          <h2 className="text-[18px] font-semibold text-[#333] leading-normal font-inter">Batch Status</h2>
          <p className="text-[14px] font-normal space-y-4 text-[#737373] leading-normal font-inter mt-[1px]">
            Manage all batches from here{" "}
          </p>

          <div className="flex items-center space-x-2 my-4">
            <Input
              placeholder="Search by batch name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={handleKeyPress}
              className="flex w-[300px] h-[33px] px-4 py-2 justify-center items-start gap-4 flex-shrink-0 text-[#646464] font-inter text-[14px] font-normal leading-normal border rounded-md"
            />
            <Button
              onClick={handleSearch}
              className="w-[79px] h-[35px] text-[#646464] font-inter text-[14px] font-medium leading-normal bg-gray-200 rounded-md hover:bg-gray-300"
            >
              Search
            </Button>
          </div>

          <div className="space-y-4">
            {batchesToDisplay.length > 0 ? (
              batchesToDisplay.map((batch) => (
                <div
                  key={batch.id}
                  className="w-[747px] p-4 rounded-lg border border-[#DEDEDE] bg-white shadow-[0px_1px_3px_rgba(34,41,48,0.15)]"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex items-start gap-2">
                      <div className="pt-[5px] pl-[5px]">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M13.3333 2H2.66667C2.29848 2 2 2.29848 2 2.66667V13.3333C2 13.7015 2.29848 14 2.66667 14H13.3333C13.7015 14 14 13.7015 14 13.3333V2.66667C14 2.29848 13.7015 2 13.3333 2ZM3.33333 12.6667V3.33333H12.6667V12.6667H3.33333Z"
                            fill="#646464"
                          />
                        </svg>
                      </div>

                      <div>
                        <h3 className="text-[18px] font-medium text-[#333] leading-normal font-inter">{batch.id}</h3>
                        <p className="text-[12px] font-medium text-[#646464] leading-normal font-inter">
                          Date Of Commencement - {formatDate(batch.Start_Date)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div>
                        <div
                          onClick={() => toggleBatchStatus(batch.id, batch)}
                          className={`w-[48px] h-[24px] flex items-center rounded-full p-1 cursor-pointer transition-colors duration-300 ${
                            batch.isEnrolledAllowed ? "bg-[#07B42D]" : "bg-gray-300"
                          }`}
                        >
                          <div
                            className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ease-in-out ${
                              batch.isEnrolledAllowed ? "translate-x-6" : "translate-x-0"
                            }`}
                          ></div>
                        </div>
                      </div>
                      <div>
                        <Button
                          onClick={() => handleViewBatch(batch)}
                          className="px-4 py-2 border border-[#D3D3D3] bg-white text-sm font-medium text-[#333] rounded-[4px]"
                        >
                          View
                        </Button>
                      </div>

                      <div>
                        <Button
                          onClick={() => handleDropdownToggle(batch.id)}
                          className="inline-flex px-4 py-2 justify-center items-center gap-3 rounded-[4px] border border-[#D3D3D3] bg-white text-sm font-medium text-[#333]"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className={`h-4 w-4 transform transition-transform ${
                              expandedBatchId === batch.id ? "rotate-180" : ""
                            }`}
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </Button>
                      </div>
                    </div>
                  </div>

                  {expandedBatchId === batch.id && (
                    <div className="mt-4 p-4 bg-[#F9F9F9] rounded-lg">
                      <div className="flex gap-8 text-sm">
                        <div>
                          Total Students: <span className="text-green-500 font-medium">{batch.totalStudents}</span>
                        </div>
                        <div>
                          Active Students: <span className="text-green-500 font-medium">{batch.activeStudents}</span>
                        </div>
                        <div>
                          Paused Students: <span className="text-amber-500 font-medium">{batch.pausedStudents}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p className="text-center text-gray-500">{search.trim() ? "No results found" : "No batches available"}</p>
            )}
          </div>

          {isLoading && (
            <div className="flex justify-center mt-4">
              <div className="w-8 h-8 border-4 border-blue-500 border-dotted rounded-full animate-spin"></div>
            </div>
          )}

          <div ref={loaderRef} className="h-10"></div>
        </div>
      </div>
    </DashboardLayout>
  )
}
